import type { CommandMap } from '@/shared/commands/command.types'

import {
  type GridNavigationDirection,
  type GridNavigationModel,
} from './grid.navigation'
import { getNextGridSelection } from './grid.navigation'
import type { GridSelection, gridSelectionActions } from './grid.store'

export type GridNavigationCommandContext = {
  selection: GridSelection
  model: GridNavigationModel
  isEditing: boolean

  select: (
    selection: GridSelection,
    options: Parameters<typeof gridSelectionActions.select>['1'],
  ) => void
}

export const gridNavigationCommands = {
  left: {
    id: 'grid.navigate.left',
    label: 'Move left',
    shortcuts: ['ArrowLeft'],

    isEnabled: (context) => canMove(context, 'left'),
    isShortcutEnabled: (context) => !context.isEditing,
    isHotkeyRepeatable: true,

    execute: (context) => move(context, 'left'),
  },

  right: {
    id: 'grid.navigate.right',
    label: 'Move right',
    shortcuts: ['ArrowRight'],

    isEnabled: (context) => canMove(context, 'right'),
    isShortcutEnabled: (context) => !context.isEditing,
    isHotkeyRepeatable: true,

    execute: (context) => move(context, 'right'),
  },

  up: {
    id: 'grid.navigate.up',
    label: 'Move up',
    shortcuts: ['ArrowUp'],

    isEnabled: (context) => canMove(context, 'up'),
    isShortcutEnabled: (context) => !context.isEditing,
    isHotkeyRepeatable: true,

    execute: (context) => move(context, 'up'),
  },

  down: {
    id: 'grid.navigate.down',
    label: 'Move down',
    shortcuts: ['ArrowDown'],

    isEnabled: (context) => canMove(context, 'down'),
    isShortcutEnabled: (context) => !context.isEditing,
    isHotkeyRepeatable: true,

    execute: (context) => move(context, 'down'),
  },
} satisfies CommandMap<GridNavigationCommandContext>

function canMove(
  context: GridNavigationCommandContext,
  direction: GridNavigationDirection,
): boolean {
  if (!context.selection) return false

  const next = getNextGridSelection(context.selection, direction, context.model)

  return !isSameSelection(context.selection, next)
}

function move(
  context: GridNavigationCommandContext,
  direction: GridNavigationDirection,
) {
  if (!context.selection) return

  const next = getNextGridSelection(context.selection, direction, context.model)

  if (isSameSelection(context.selection, next)) return

  context.select(next, {
    focus: true,
    scrollIntoView: true,
  })
}

function isSameSelection(left: GridSelection, right: GridSelection): boolean {
  if (left === right) return true
  if (!left || !right) return false
  if (left.type !== right.type) return false

  if (left.type === 'cue' && right.type === 'cue') {
    return left.cueId === right.cueId
  }

  if (left.type === 'cell' && right.type === 'cell') {
    return left.cellKey === right.cellKey
  }

  return false
}
